import React, { useState, useCallback } from "react";
import _ from "lodash";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Typography,
  Grid,
  CircularProgress,
  TextField,
  Button,
} from "@material-ui/core";
import DoneIcon from "@material-ui/icons/Done";
import CloseIcon from "@material-ui/icons/Close";
import TimePicker from "./TimePicker";
import { makeStyles } from "@material-ui/core/styles";
import ToggleButton from "@material-ui/lab/ToggleButton";
import ToggleButtonGroup from "@material-ui/lab/ToggleButtonGroup";
import Alert from "@material-ui/lab/Alert";
import ReportProblemOutlinedIcon from "@material-ui/icons/ReportProblemOutlined";

import api from "../../../store/api";

const useStyles = makeStyles({
  root: {
    maxWidth: "370px",
    backgroundColor: "#fff",
  },
  headerTitle: {
    fontFamily: "Lato",
    fontSize: "16px",
    fontWeight: 900,
    lineHeight: "19px",
    color: "#333333",
    paddingTop: "7px",
  },
  closeIcon: {
    color: "#333333",
    position: "absolute",
    top: "8px",
    right: "8px",
  },
  saveButton: {
    marginRight: "16px",
    marginTop: "19px",

    "@media (max-width: 1024px)": {
      marginTop: "0",
    },
  },
  dialogContent: {
    paddingTop: "9px",
  },
  description: {
    fontSize: "16px",
    lineHeight: "19px",
    color: "#333333",
    marginBottom: "24px",
  },
  pickupDropoffContainer: {
    width: "100%",
  },
  pickupDropoffRoof: {
    color: "#02489D !important",
    backgroundColor: "#FFFFFF !important",
    border: "1px solid #02489D !important",
    width: "100%",
    borderRadius: "30px",
  },
  pickupDropoffSelected: {
    color: "#FFFFFF !important",
    backgroundColor: "#02489D !important",
  },
  pickupDropoffLabel: {
    fontWeight: 600,
    fontSize: "14px",
    lineHeight: "17px",
    textAlign: "center",
    padding: "6px 0 7px",
  },
  label: {
    fontWeight: "bold",
    fontSize: "13px",
    lineHeight: "16px",
    color: "#333333",
    margin: "20px 0 10px",
  },
  textfield: {
    background: "#E8E8E8 !important",

    "& > div": {
      padding: "10px 12px",
    },

    "& textarea": {
      color: "#333",
      padding: "0 5px",
      fontSize: "16px",
    },
  },
  requestButton: {
    padding: "14px 15px",
    color: "#fff",
    backgroundColor: "#02489D !important",
    borderColor: "#02489D",
    fontSize: "18px",
    lineHeight: "22px",
    height: "unset",
    borderRadius: "4px",
    marginLeft: "20px !important",
    minWidth: "unset",
  },
  doneButton: {
    padding: "14px 15px",
    color: "#fff",
    backgroundColor: "#02489D !important",
    borderColor: "#02489D",
    fontSize: "18px",
    lineHeight: "22px",
    height: "unset",
    borderRadius: "4px",
    minWidth: "unset",
  },
  cancelButton: {
    padding: "13px 15px",
    color: "#02489D",
    borderColor: "#02489D",
    fontSize: "18px",
    lineHeight: "22px",
    borderRadius: "4px",
    minWidth: "unset",
  },
  actionButtons: {
    paddingBottom: "31px",
    display: "flex",
    flexWrap: "wrap",
  },
  buttonWrapper: {
    flex: "1 0 100%",
    display: "flex",
  },
  iconBox: {
    width: 112,
    height: 112,
    borderRadius: 112,
    backgroundColor: "#3FA052",
    margin: "60px auto 57px",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
  },
  icon: {
    color: "white",
    fontSize: "5rem",
    paddingTop: "3px",
    paddingLeft: "3px",
  },
  successMessage: {
    fontWeight: "bold",
    fontSize: "16px",
    lineHeight: "19px",
    textAlign: "center",
    color: "#333333",
  },
  errorMessage: {
    marginBottom: "15px",
    flex: "1 0 90%",
  },
  spinner: {
    marginRight: 13,
    color: "white",
    marginLeft: "-14px",
  },
});

const PICKUP_OPTION = "PICKUP";
const DROPOFF_OPTION = "DROPOFF";

const calcInitTime = (pickUpTime: any, dropOffTime: any) => {
  if (pickUpTime) {
    const now = new Date();
    now.setHours(pickUpTime.hours);
    now.setMinutes(pickUpTime.minutes);
    return now
  }
  if (dropOffTime) {
    const now = new Date();
    now.setHours(dropOffTime.hours);
    now.setMinutes(dropOffTime.minutes);
    return now
  }
  return null;
};

const AddHocModalRequest = ({ onClose, modalData }: any) => {
  const [errorText, setErrorText] = useState<string | null>(null);
  const [isSavingInProgress, setIsSavingInProgress] = useState(false);
  const [pickupOrDropoff, setPickupOrDropoff] = useState(modalData?.dropoffBeforeTime || modalData?.dropoffAfterTime ? DROPOFF_OPTION : PICKUP_OPTION);
  const [timeFrom, setTimeFrom] = useState<Date | null>(calcInitTime(modalData?.pickupAfterTime, modalData?.dropoffAfterTime));
  const [timeTo, setTimeTo] = useState<Date | null>(calcInitTime(modalData?.pickupBeforeTime, modalData?.dropoffBeforeTime));
  const [comments, setComments] = useState("");
  const [priorityOption, setPriorityOption] = useState(modalData?.priority ? modalData?.priority : 1);
  const [dataSaved, setDataSaved] = useState(false);
  const classes = useStyles();

  const sendAdHocRequest = async () => {
    setIsSavingInProgress(true);
    setErrorText(null);
    if (!timeFrom || !timeTo) {
      setErrorText("From and to time can't be empty");
      setIsSavingInProgress(false);
      return;
    }
    try {
      const requestBody: {
        transferId: string;
        priority: number;
        comments: string | null;
        pickupFrom: string | null;
        pickupTo: string | null;
        dropOffFrom: string | null;
        dropOffTo: string | null;
      } = {
        transferId: modalData?.transferId,
        priority: priorityOption,
        comments: comments,
        pickupFrom: null,
        pickupTo: null,
        dropOffFrom: null,
        dropOffTo: null,
      };
      if (pickupOrDropoff === PICKUP_OPTION) {
        requestBody.pickupFrom = timeFrom ? `${timeFrom.getHours()}:${timeFrom.getMinutes()}:00` : null;
        requestBody.pickupTo = timeTo ? `${timeTo.getHours()}:${timeTo.getMinutes()}:00` : null;
      } else if (pickupOrDropoff === DROPOFF_OPTION) {
        requestBody.dropOffFrom = timeFrom ? `${timeFrom.getHours()}:${timeFrom.getMinutes()}:00` : null;
        requestBody.dropOffTo = timeTo ? `${timeTo.getHours()}:${timeTo.getMinutes()}:00` : null;
      }
      const res = await api.requestAdHocTransfer("", requestBody);
      if (res.hasError) {
        throw new Error();
      }
      setDataSaved(true);
    } catch (error) {
      setErrorText("There was a problem sending your request, please try again.");
    }

    setIsSavingInProgress(false);
  };

  return (
    <Dialog open={true} onClose={onClose} maxWidth="xs" classes={{ paper: classes.root }}>
      <DialogTitle>
        <Typography className={classes.headerTitle}>Request an ad-hoc transfer</Typography>
        <IconButton aria-label="close" onClick={onClose} className={classes.closeIcon}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      {dataSaved ? (
        <>
          <DialogContent
            classes={{
              root: classes.dialogContent,
            }}
          >
            <div className={classes.iconBox}>
              <DoneIcon className={classes.icon} />
            </div>
            <div className={classes.successMessage}>
              <p>Thanks for requesting an ad-hoc transport. Our team have received your request and are working to source a vehicle for it.</p>
              <p>This transfer will appear as assigned when a vehicle is confirmed.</p>
            </div>
          </DialogContent>

          <DialogActions
            classes={{
              root: classes.actionButtons,
            }}
          >
            <Button variant="outlined" fullWidth onClick={onClose} disableRipple disableTouchRipple className={classes.doneButton}>
              Done
            </Button>
          </DialogActions>
        </>
      ) : (
        <>
          <DialogContent
            classes={{
              root: classes.dialogContent,
            }}
          >
            <Grid container>
              <Grid item xs={12}>
                <Typography className={classes.description}>Choose a pickup or dropoff time window for this transfer.</Typography>
                <ToggleButtonGroup
                  color="primary"
                  exclusive
                  onChange={(e, v) => {
                    setPickupOrDropoff(v);
                  }}
                  size={"small"}
                  value={pickupOrDropoff}
                  classes={{
                    root: classes.pickupDropoffContainer,
                  }}
                >
                  <ToggleButton
                    value={PICKUP_OPTION}
                    disableRipple
                    classes={{
                      root: classes.pickupDropoffRoof,
                      selected: classes.pickupDropoffSelected,
                      label: classes.pickupDropoffLabel,
                    }}
                  >
                    Pickup
                  </ToggleButton>
                  <ToggleButton
                    value={DROPOFF_OPTION}
                    disableRipple
                    classes={{
                      root: classes.pickupDropoffRoof,
                      selected: classes.pickupDropoffSelected,
                      label: classes.pickupDropoffLabel,
                    }}
                  >
                    Dropoff
                  </ToggleButton>
                </ToggleButtonGroup>
              </Grid>

              <Grid item xs={6}>
                <Typography className={classes.label}>FROM</Typography>
                <TimePicker timeValue={timeFrom} setTimeValue={setTimeFrom} />
              </Grid>
              <Grid item xs={6}>
                <Typography className={classes.label}>TO</Typography>
                <TimePicker timeValue={timeTo} setTimeValue={setTimeTo} />
              </Grid>
              <Grid item xs={12}>
                <Typography className={classes.label}>PRIORITY</Typography>
                <ToggleButtonGroup
                  color="primary"
                  exclusive
                  onChange={(e, v) => {
                    setPriorityOption(v);
                  }}
                  size={"small"}
                  value={priorityOption}
                  classes={{
                    root: classes.pickupDropoffContainer,
                  }}
                >
                  <ToggleButton
                    value={1}
                    disableRipple
                    classes={{
                      root: classes.pickupDropoffRoof,
                      selected: classes.pickupDropoffSelected,
                      label: classes.pickupDropoffLabel,
                    }}
                  >
                    1
                  </ToggleButton>
                  <ToggleButton
                    value={2}
                    disableRipple
                    classes={{
                      root: classes.pickupDropoffRoof,
                      selected: classes.pickupDropoffSelected,
                      label: classes.pickupDropoffLabel,
                    }}
                  >
                    2
                  </ToggleButton>
                  <ToggleButton
                    value={3}
                    disableRipple
                    classes={{
                      root: classes.pickupDropoffRoof,
                      selected: classes.pickupDropoffSelected,
                      label: classes.pickupDropoffLabel,
                    }}
                  >
                    3
                  </ToggleButton>
                </ToggleButtonGroup>

                <Typography className={classes.label}>COMMENTS (OPTIONAL)</Typography>

                <TextField
                  id="standard-textarea"
                  multiline
                  variant="filled"
                  value={comments}
                  InputProps={{ disableUnderline: true }}
                  onChange={(event: any) => {
                    setComments(event.target.value);
                  }}
                  rows={4}
                  classes={{
                    root: classes.textfield,
                  }}
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions
            classes={{
              root: classes.actionButtons,
            }}
          >
            {errorText && (
              <Alert variant="filled" icon={<ReportProblemOutlinedIcon />} severity="error" classes={{ root: classes.errorMessage }}>
                {errorText}
              </Alert>
            )}
            <div className={classes.buttonWrapper}>
              <Button variant="outlined" fullWidth onClick={onClose} className={classes.cancelButton} disableRipple disableTouchRipple>
                Cancel
              </Button>
              <Button
                variant="outlined"
                fullWidth
                onClick={sendAdHocRequest}
                disabled={isSavingInProgress}
                className={classes.requestButton}
                disableRipple
                disableTouchRipple
              >
                {isSavingInProgress && <CircularProgress className={classes.spinner} size={20} />}
                Request
              </Button>
            </div>
          </DialogActions>
        </>
      )}
    </Dialog>
  );
};

export default AddHocModalRequest;
